import React, { useState, useEffect, useMemo } from 'react';
import {
  useFeathers,
  mergeReferencesWithDraft
} from '../../app/util';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import moment from 'moment';

import {
  ReferenceEditor,
  ReviewProgress,
  TaskPage,
  EditCandidateProfile,
  mapDraftToServerInputs,
  newDraftAfterError,
  TimeTracker,
  StarRanker,
} from '../../components';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Link } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { SCRAPED_CONTENT_METADATA_FIELDS } from '../../app/feathers/scraped-content/scraped-content';

const PublishCandidate = () => {
  const feathers = useFeathers();
  const user = useSelector(state => state.user);
  const [task, setTask] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [race, setRace] = useState(null);
  const [saving, setSaving] = useState(false); 
  const [taskPageSaving, setTaskPageSaving] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [stagedUpdatesToReferences, setStagedUpdatesToReferences] = useState(null);
  const [showingFeedbackDialog, setShowingFeedbackDialog] = useState(false);
  const [trackedTimesByArea, setTrackedTimesByArea] = useState({});
  const [scrapedContent, setScrapedContent] = useState([]);
  /** Optional 1-5 quality score for researcher performance (submitted when publishing). */
  const [reviewScore, setReviewScore] = useState(null);

  /* Candidate being actively edited */
  const [candidateEdit, setCandidateEdit] = useState({});

  // Apply $scrapedContentChanges to scrapedContent to create scrapedContentMapped
  const scrapedContentMapped = useMemo(() => {
    if (!scrapedContent) {
      return scrapedContent;
    }

    if (!candidateEdit?.$scrapedContentChanges) {
      return scrapedContent;
    }

    const changesMap = new Map(
      candidateEdit.$scrapedContentChanges.map(change => [
        change.scrapedContentId,
        change.changes
      ])
    );

    // Filter out deleted items and apply type changes
    return scrapedContent
      .filter(sc => {
        const changes = changesMap.get(sc._id);
        // Filter out items marked for deletion
        return !changes || changes.delete !== true;
      })
      .map(sc => {
        const changes = changesMap.get(sc._id);
        if (!changes || changes.type === undefined) {
          return sc;
        }

        // Apply the changes to create a new scrapedContent object
        return {
          ...sc,
          type: changes.type
        };
      });
  }, [scrapedContent, candidateEdit?.$scrapedContentChanges]);

  const handleScrapedContentSaved = (updated) => {
    setScrapedContent(existing => existing.map(item => (
      item._id === updated._id
        ? {
            ...item,
            status: updated.status,
            error: updated.error,
            addMethod: updated.addMethod,
            isMostRecent: updated.isMostRecent,
            updatedAt: updated.updatedAt,
          }
        : item
    )));
  };

  useEffect(() => {
    if (stagedUpdatesToReferences && candidateEdit) {
      setStagedUpdatesToReferences(null);
      setCandidateEdit(
        mergeReferencesWithDraft(
          stagedUpdatesToReferences,
          candidateEdit
        )
      );
    }
  }, [candidateEdit, stagedUpdatesToReferences, task]);

  const createFeedback = async (loadNewTask) => {
    if (feedbackSaving) return;
    setFeedbackSaving(true);

    try {
      // first, load the new candidate after publishing the updates 
      const afterDraft = await feathers.getService('candidates').get(candidate._id);

      const feedback = await feathers.getService('profile-feedbacks').create({
				candidate: candidate?._id,
				versionNumberBefore: draftVersionNumber,
				versionNumberAfter: afterDraft.stagingDraft.version
			});
      console.log(`Feedback created`)
      const linkToFeedback = `/elections/${candidate?.election?.key || candidate.election}/races/${candidate?.race}/candidates/${candidate?._id}/feedback/${feedback._id}?confirm=true`
      console.log(linkToFeedback)

      window.open(linkToFeedback, "_blank");
    } catch (err) {
      console.error(`Error in creating feedback: `, err);
    } finally {
      setFeedbackSaving(false)
      loadNewTask()
      setShowingFeedbackDialog(false)
    }
  }

  const onCheckoutTask = async (task, loadNextTask) => {
    setCandidate(null);
    setCandidateEdit(null);
    setRace(null);
    setFeedbackSaving(false);
    setReviewScore(null);
    setTask(task);
    let candidate;
    try {
      candidate = await feathers.getService('candidates').get(task.details.candidate);
    } catch(err) {
      if(err.name === 'NotFound') {
        // bad candidate, we need to cancel this task
        console.log('Candidate not found')
        await feathers.getService('research-tasks').patch(task._id, {
          $cancelTask: true
        });
        setTask(null);
        loadNextTask()
        return
      } else {
        throw err;
      }
    }
    setCandidate(candidate);
    const { updatedAt: holdingDataUpdatedAt, ...holdingData } = task?.holdingData || {};
    // seed inputs with most recently updated data from holdingData or candidate record
    const candidateDraft = (
      ((holdingData && holdingDataUpdatedAt > candidate?.updatedAt)
        ? holdingData
        : candidate?.stagingDraft) 
      || {}
    )

    setCandidateEdit({
      ...candidateDraft,
      removedAutoContent: candidateDraft.removedAutoContent || candidate.removedAutoContent,
    });
    const race = await feathers
      .getService('races')
      .get(candidate.race);
    setRace(race);

    // Load scraped content
    loadScrapedContent(candidate._id);
  };

  const loadScrapedContent = async (candidateId) => {
    try {
      const scrapedContentCount = await feathers.getService('scraped-content').find({
        query: {
          candidateId,
          $limit: 0,
        }
      });
      const numChunks = Math.ceil(scrapedContentCount.total / 10);
      const chunks = Array.from({ length: numChunks }, (_, i) => i);
      const scrapedContent = (await Promise.all(chunks.map(i => {
        return feathers.getService('scraped-content').find({
          query: {
            candidateId,
            $limit: 10,
            $skip: i * 10,
            $sort: { createdAt: -1 },
            $select: SCRAPED_CONTENT_METADATA_FIELDS,
          }
        })
      }))).map(res => res.data).flat();

      setScrapedContent(scrapedContent);
    } catch(err) {
      console.log(`Failed to load scraped content: `, err);
    }
  };

  useEffect(() => {
    if (candidate && candidateEdit) {
      const base = mapDraftToServerInputs(candidateEdit);
      const withIssuesToCoverAdditional = candidateEdit.issuesToCoverAdditional
        ? { issuesToCoverAdditional: candidateEdit.issuesToCoverAdditional }
        : {};
      setCompletionData({
        ...base,
        ...withIssuesToCoverAdditional,
      });
    }
  }, [candidate, candidateEdit, task]);

  const onCompleteError = (e) => {
    setCandidate((c) => newDraftAfterError(c, e));
  };

  const [submittedByUser, draftVersionNumber] = useMemo(() => {
    if(!task || !candidate) return [null, null];
    let userToRef = task.details?.submittedBy;

    // find first non-user who worked on this
    const draftsPossible = [
      candidate.stagingDraft,
      ...(candidate.previousDrafts || [])
    ].filter(Boolean)

    const draft = draftsPossible.find(d => userToRef ? d?.user?._id === userToRef : (d?.user?._id && d?.user?._id !== user._id));
    return [ draft?.user, draft?.version ];
  }, [ task, user, candidate ])

  const submittedByUserName = useMemo(() => {
    return (submittedByUser?.firstName || submittedByUser?.lastName)
      ? [submittedByUser?.firstName, submittedByUser?.lastName].filter(Boolean).join(' ')
      : (submittedByUser?.name || submittedByUser?.email || '--')
  }, [ submittedByUser ])

  const originalResearchTimeLabel = useMemo(() => {
    const seconds = task?.details?.originalResearchTimeSeconds;
    if (typeof seconds !== 'number' || Number.isNaN(seconds) || seconds < 0) return '--';

    const duration = moment.duration(seconds, 'seconds');
    const hours = duration.hours();
    const minutes = duration.minutes();
    const secondsRemainder = duration.seconds();
    return `${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m ` : ''}${secondsRemainder}s`;
  }, [task]);

  // Handler for receiving tracked times from EditCandidateProfile
  const handleTrackedTimesUpdate = (times) => {
    setTrackedTimesByArea(times);
  };

  return (
    <TaskPage
      task={task}
      disableTimeTracker
      taskType="publish-candidate"
      onCheckoutTask={onCheckoutTask}
      completionData={completionData
        ? {
          ...completionData,
          trackedTimesByArea: trackedTimesByArea,
          ...(reviewScore != null && { reviewScore }),
        }
        : completionData
      }
      holdingData={candidateEdit}
      onCompleteError={onCompleteError}
      completeButtonLabel='Publish'
      sidePaneConfig={[
        {
          title: 'Submitted by',
          content: (
            <>
              <Typography variant="body2" style={{ marginTop: '6px' }}>You're reviewing a profile submitted by</Typography>
              <Typography variant="h3" style={{ marginTop: '8px' }}>
                {submittedByUserName}
              </Typography>
              {task?.createdAt && (
                <>
                  <Typography variant="body2" style={{ marginTop: '12px' }}>Submitted on</Typography>
                  <Typography variant="body1" style={{ marginTop: '4px' }}>
                    {moment(task.createdAt).format('LLL')}
                  </Typography>
                </>
              )}
              <Typography variant="body2" style={{ marginTop: '12px' }}>Original research time</Typography>
              <Typography variant="body1" style={{ marginTop: '4px' }}>
                {originalResearchTimeLabel}
              </Typography>
              <Typography variant="body2" style={{ marginTop: '12px' }}>Quality score (optional)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <StarRanker
                  value={reviewScore ?? 0}
                  onChange={(n) => setReviewScore(n)}
                  detailMenuOnOneStar={false}
                />
                {reviewScore != null && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={() => setReviewScore(null)}
                    sx={{ cursor: 'pointer' }}
                  >
                    Clear
                  </Link>
                )}
              </Box>
            </>
          ),
          initiallyExpanded: true,
        },
        {
          title: 'Priority',
          content: (
            <TimeTracker
              priorityLevel={candidate?.priorityLevel}
              seedTask={task}
              benchmarkMode='review'
              coveragePlan={candidate?.coveragePlan}
            />
          ),
          initiallyExpanded: true,
        },
        Boolean(candidate?.coveragePlan) && candidate.coveragePlan === 'auto-coverage' && {
          title: 'Progress',
          content: (
            <ReviewProgress
              coveragePlan={candidate?.coveragePlan}
              candidate={candidateEdit}
              key={`Progress`}
            />
          ),
          initiallyExpanded: true,
        },
        Boolean(candidateEdit?.references) && {
          title: 'Sources',
          content: (
            <ReferenceEditor
              references={candidateEdit?.references}
              onChangeReferences={setStagedUpdatesToReferences}
              onChangeActiveDraft={(update, scrapedContentChanges) => setCandidateEdit({
                ...candidateEdit, 
                ...update,
                ...(scrapedContentChanges ? { $scrapedContentChanges: scrapedContentChanges } : {})
              })}
              key="Information sources"
              disabled={saving} // being set in child via handleParentSaving
              activeDraft={candidateEdit}
              scrapedContent={scrapedContentMapped}
              onScrapedContentSaved={handleScrapedContentSaved}
            />
          ),
          initiallyExpanded: true,
        },
      ].filter(Boolean)}
      onCompleteTaskBeforeLoadNext={(draftVersionNumber && candidate.coveragePlan !== 'basic-coverage') ? () => setShowingFeedbackDialog(true) : null}
      onCompleteTaskDialog={
        (loadNextTask) => (
          <Dialog
            open={showingFeedbackDialog}
            onClose={() => {
              loadNextTask()
              setShowingFeedbackDialog(false)
            }}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-body'
          >
            <DialogTitle id='alert-dialog-title'>
              Give feedback?
            </DialogTitle>
            <DialogContent>
              <DialogContentText id='alert-dialog-body'>
                Do you want to give feedback to {submittedByUserName} on their profile?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  loadNextTask()
                  setShowingFeedbackDialog(false)
                }}
                color='primary'
              >
                Skip
              </Button>
              <Button 
                onClick={() => createFeedback(loadNextTask)} 
                color='primary' 
                autoFocus
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        )
      }
    >
      <WrapperInner>
        {
          candidateEdit && (
            <EditCandidateProfile
              coveragePlan={candidate?.coveragePlan}
              value={candidateEdit}
              onChange={setCandidateEdit}
              onTrackedTimesUpdate={handleTrackedTimesUpdate}
              onChangeIssue={(newIssue, issueKey) => {
                setCandidateEdit(existingDraft => {
                  const existingDraftIssues = existingDraft.issues || [];
                  const newIssues = existingDraftIssues.map((issue, i) => {
                    if(issue.key === issueKey) {
                      return newIssue
                    }
                    return issue
                  })

                  return {
                    ...existingDraft,
                    issues: newIssues
                  }
                })
              }}
              candidateId={candidate?._id}
              photoOptions={candidate?.photoOptions}
              race={race}
              disabled={saving || taskPageSaving} // being set in child
              electionKey={
                candidate?.election?.key || candidate?.election
              }
            />
          )
        }
        
      </WrapperInner>
    </TaskPage>
  );
};

const WrapperInner = styled.div`
  padding: 24px 24px 0;
`;
export default PublishCandidate;
